<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <div class="flex items-center space-x-3">
                <UButton to="/broadcasts" color="white" icon="i-ph-arrow-left" size="sm" />
                <h1 class="text-2xl font-semibold text-gray-700">Templates</h1>
            </div>
            <UButton @click="openCreate()" color="black" icon="i-ph-plus-bold">New Template</UButton>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="latestVersions"
                :columns="columns"
                :loading="loading"
                :empty-state="{ icon: 'i-ph-file-text-duotone', label: 'No templates.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm' }"
                class="h-full"
            >
                <template #name-data="{ row }">
                    <p class="font-medium">{{ row.name }}</p>
                    <p class="text-xs text-gray-500">v{{ row.version }}</p>
                </template>

                <template #status-data="{ row }">
                    <UBadge :color="statusColor(row.status)" variant="subtle">{{ row.status }}</UBadge>
                    <p v-if="row.rejection_reason" class="text-xs text-red-500 mt-1">{{ row.rejection_reason }}</p>
                </template>

                <template #actions-data="{ row }">
                    <div class="flex items-center gap-1">
                        <UButton size="xs" color="gray" variant="ghost" :loading="busy === row.id" @click="edit(row)">
                            {{ row.locked_at ? 'Fork' : 'Edit' }}
                        </UButton>
                        <UButton v-if="canSubmit(row)" size="xs" color="gray" variant="ghost" :loading="busy === row.id" @click="submit(row)">
                            Submit
                        </UButton>
                        <UButton v-if="row.twilio_content_sid" size="xs" color="gray" variant="ghost" :loading="busy === row.id" @click="refresh(row)">
                            Refresh
                        </UButton>
                        <UButton v-if="row.channel !== 'whatsapp' && row.status === 'draft'" size="xs" color="gray" variant="ghost" @click="markReady(row)">
                            Publish
                        </UButton>
                        <UButton size="xs" color="red" variant="ghost" @click="archive(row)">Archive</UButton>
                    </div>
                </template>
            </UTable>
        </div>

        <OverlayTemplateForm v-model:open="formOpen" :template="editing" @saved="load()" />
    </div>
</template>

<script setup lang="ts">
import type { Template, TemplateStatus } from '~/types/template'

definePageMeta({ middleware: 'auth', roles: ['admin'] })

const toast = useToast()
const loading = ref(false)
const busy = ref<string | null>(null)
const templates = ref<Template[]>([])
const formOpen = ref(false)
const editing = ref<Template | null>(null)

const columns = [
    { key: 'name', label: 'Name' },
    { key: 'channel', label: 'Channel' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
]

const latestVersions = computed(() => {
    const byFamily = new Map<string, Template>()
    for (const t of templates.value) {
        if (t.status === 'archived') continue
        const seen = byFamily.get(t.family_id)
        if (!seen || t.version > seen.version) byFamily.set(t.family_id, t)
    }
    return [...byFamily.values()]
})

function statusColor(status: TemplateStatus): string {
    if (status === 'approved' || status === 'ready') return 'green'
    if (status === 'pending') return 'amber'
    if (status === 'rejected' || status === 'disabled') return 'red'
    if (status === 'paused') return 'orange'
    return 'gray'
}

function canSubmit(t: Template): boolean {
    return t.channel === 'whatsapp' && t.status === 'draft'
}

async function load() {
    loading.value = true
    try {
        templates.value = await $fetch<Template[]>('/api/templates')
        await refreshPending()
    } finally {
        loading.value = false
    }
}

async function refreshPending() {
    const pending = templates.value.filter(t => t.status === 'pending' && t.twilio_content_sid)
    if (!pending.length) return
    const results = await Promise.allSettled(
        pending.map(t => $fetch<Template>(`/api/templates/${t.id}/refresh`, { method: 'POST' })),
    )
    const updated = results.flatMap(r => (r.status === 'fulfilled' ? [r.value] : []))
    if (updated.length) {
        templates.value = templates.value.map(t => updated.find(u => u.id === t.id) ?? t)
    }
}

function openCreate() {
    editing.value = null
    formOpen.value = true
}

async function edit(t: Template) {
    if (!t.locked_at) {
        editing.value = t
        formOpen.value = true
        return
    }
    busy.value = t.id
    try {
        const forked = await $fetch<Template>(`/api/templates/${t.id}/fork`, { method: 'POST' })
        await load()
        editing.value = forked
        formOpen.value = true
    } catch (e: any) {
        toast.add({ title: 'Fork failed', description: e?.data?.message ?? e?.message, color: 'red' })
    } finally {
        busy.value = null
    }
}

async function submit(t: Template) {
    busy.value = t.id
    try {
        await $fetch(`/api/templates/${t.id}/submit`, { method: 'POST' })
        toast.add({ title: 'Submitted for WhatsApp approval' })
        await load()
    } catch (e: any) {
        toast.add({ title: 'Submit failed', description: e?.data?.message ?? e?.message, color: 'red' })
    } finally {
        busy.value = null
    }
}

async function refresh(t: Template) {
    busy.value = t.id
    try {
        await $fetch(`/api/templates/${t.id}/refresh`, { method: 'POST' })
        await load()
    } catch (e: any) {
        toast.add({ title: 'Refresh failed', description: e?.data?.message ?? e?.message, color: 'red' })
    } finally {
        busy.value = null
    }
}

async function markReady(t: Template) {
    try {
        await $fetch(`/api/templates/${t.id}`, { method: 'PATCH', body: { status: 'ready' } })
        await load()
    } catch (e: any) {
        toast.add({ title: 'Publish failed', description: e?.data?.message ?? e?.message, color: 'red' })
    }
}

async function archive(t: Template) {
    try {
        await $fetch(`/api/templates/${t.id}/archive`, { method: 'POST' })
        await load()
    } catch (e: any) {
        toast.add({ title: 'Archive failed', description: e?.data?.message ?? e?.message, color: 'red' })
    }
}

onMounted(load)
</script>
